import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const CFG_VACIA = {
  hoja_extracto: 'DEBITOS', col_fecha_extracto: '4',
  col_valor_extracto: '6', col_concepto_extracto: '7',
  extracto_dos_columnas: false, col_debito_extracto: '6', col_credito_extracto: '7',
  hoja_auxiliar: 'DEBITOS', col_fecha_auxiliar: '1',
  col_debito_auxiliar: '7', col_credito_auxiliar: '8',
  tolerancia_dias: '0',
};

const CUENTA_VACIA = {
  nombre: '', empresa: '', nit: '', banco: '', tipo_cuenta: 'CORRIENTE',
  numero_cuenta: '', codigo: '', responsable: '', cargo: '', config: CFG_VACIA,
};

export default function Cuentas() {
  const { user, logout, API } = useAuth();
  const [cuentas, setCuentas] = useState([]);
  const [form, setForm] = useState(CUENTA_VACIA);
  const [editId, setEditId] = useState(null);  // null = nueva
  const [mostrarForm, setMostrarForm] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { cargar(); }, []);

  const cargar = () =>
    axios.get(`${API}/cuentas`).then(r => setCuentas(r.data)).catch(() => {});

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleCfg = e => setForm(f => ({ ...f, config: { ...f.config, [e.target.name]: e.target.value } }));
  const toggleDosColumnas = e => setForm(f => ({ ...f, config: { ...f.config, extracto_dos_columnas: e.target.checked } }));

  const abrir = (cuenta) => {
    if (cuenta) {
      setForm({ ...cuenta, config: { ...CFG_VACIA, ...cuenta.config } });
      setEditId(cuenta.id);
    } else {
      setForm(CUENTA_VACIA);
      setEditId(null);
    }
    setMostrarForm(true);
    setMsg('');
  };

  const guardar = async () => {
    if (!form.nombre.trim() || !form.banco.trim()) {
      setMsg('Nombre y banco son obligatorios');
      return;
    }
    setGuardando(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'config') fd.append('config_json', JSON.stringify(v));
        else fd.append(k, v);
      });
      if (editId) {
        await axios.put(`${API}/cuentas/${editId}`, fd);
      } else {
        await axios.post(`${API}/cuentas`, fd);
      }
      await cargar();
      setMostrarForm(false);
      setMsg('');
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta cuenta bancaria?')) return;
    await axios.delete(`${API}/cuentas/${id}`);
    cargar();
  };

  const bancoColor = { BANCOLOMBIA: '#f59e0b', DAVIVIENDA: '#dc2626', BBVA: '#2563eb' };
  const bancoIco  = b => bancoColor[b?.toUpperCase()] ? '🏦' : '🏦';

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <span style={s.brand}>🏦 ConciliaApp</span>
        <div style={s.navRight}>
          <a href="/" style={s.navLink}>⚡ Conciliar</a>
          <a href="/kpis" style={s.navLink}>📊 KPIs</a>
          <a href="/historial" style={s.navLink}>📋 Historial</a>
          <a href="/calendario-tributario" style={s.navLink}>📅 Calendario</a>
          <a href="/retencion-fuente" style={s.navLink}>🧾 Retención</a>
          {user?.rol === 'admin' && <a href="/admin" style={s.navLink}>Admin</a>}
          <span style={s.navUser}>👤 {user?.nombre}</span>
          <button onClick={logout} style={s.navBtn}>Salir</button>
        </div>
      </nav>

      <div style={s.content}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>🏦 Cuentas bancarias</h1>
            <p style={s.subtitle}>Guarda los datos de cada cuenta una sola vez y selecciónala al conciliar.</p>
          </div>
          <button style={s.btnPrimary} onClick={() => abrir(null)}>+ Nueva cuenta</button>
        </div>

        {cuentas.length === 0 && !mostrarForm && (
          <div style={s.empty}>
            <div style={{ fontSize: 48 }}>🏦</div>
            <p>No tienes cuentas bancarias guardadas.</p>
            <button style={s.btnPrimary} onClick={() => abrir(null)}>Crear primera cuenta</button>
          </div>
        )}

        <div style={s.grid}>
          {cuentas.map(c => (
            <div key={c.id} style={s.card}>
              <div style={s.cardHead}>
                <span style={s.cardIcon}>{bancoIco(c.banco)}</span>
                <div>
                  <div style={s.cardNombre}>{c.nombre}</div>
                  <div style={s.cardBanco}>{c.banco} · {c.tipo_cuenta}</div>
                </div>
              </div>
              <div style={s.cardBody}>
                {c.empresa && <Row label="Empresa" val={c.empresa} />}
                {c.nit      && <Row label="NIT"     val={c.nit} />}
                {c.numero_cuenta && <Row label="Cuenta" val={c.numero_cuenta} />}
                {c.responsable   && <Row label="Responsable" val={c.responsable} />}
              </div>
              <div style={s.cardFoot}>
                <button style={s.btnEdit} onClick={() => abrir(c)}>✏️ Editar</button>
                <button style={s.btnDel}  onClick={() => eliminar(c.id)}>🗑️ Eliminar</button>
              </div>
            </div>
          ))}
        </div>

        {/* ── Formulario ── */}
        {mostrarForm && (
          <div style={s.formWrap}>
            <h2 style={s.formTitle}>{editId ? 'Editar cuenta' : 'Nueva cuenta bancaria'}</h2>

            <div style={s.section}>Datos de la cuenta</div>
            <div style={s.formGrid}>
              {[['nombre','Nombre de la cuenta *'],['empresa','Empresa'],['nit','NIT'],
                ['banco','Banco *'],['tipo_cuenta','Tipo de cuenta'],['numero_cuenta','Número de cuenta'],
                ['codigo','Código'],['responsable','Responsable'],['cargo','Cargo']
              ].map(([k,lbl]) => (
                <div key={k} style={s.field}>
                  <label style={s.label}>{lbl}</label>
                  <input style={s.input} name={k} value={form[k]} onChange={handle} />
                </div>
              ))}
            </div>

            <div style={s.section}>Configuración de columnas (extracto)</div>
            <label style={s.checkboxRow}>
              <input type="checkbox" checked={!!form.config.extracto_dos_columnas} onChange={toggleDosColumnas} />
              El extracto trae entradas y salidas en columnas separadas (débito/crédito)
            </label>
            <div style={s.formGrid}>
              {[['hoja_extracto','Hoja extracto'],['col_fecha_extracto','Col. fecha'],
                ...(form.config.extracto_dos_columnas
                  ? [['col_debito_extracto','Col. débito (salidas)'],['col_credito_extracto','Col. crédito (entradas)']]
                  : [['col_valor_extracto','Col. valor']]),
                ['col_concepto_extracto','Col. concepto']
              ].map(([k,lbl]) => (
                <div key={k} style={s.field}>
                  <label style={s.label}>{lbl}</label>
                  <input style={s.input} name={k} value={form.config[k] ?? ''} onChange={handleCfg} />
                </div>
              ))}
            </div>

            <div style={s.section}>Configuración de columnas (auxiliar)</div>
            <div style={s.formGrid}>
              {[['hoja_auxiliar','Hoja auxiliar'],['col_fecha_auxiliar','Col. fecha'],
                ['col_debito_auxiliar','Col. débito'],['col_credito_auxiliar','Col. crédito'],
                ['tolerancia_dias','Tolerancia días'],
              ].map(([k,lbl]) => (
                <div key={k} style={s.field}>
                  <label style={s.label}>{lbl}</label>
                  <input style={s.input} name={k} value={form.config[k] ?? ''} onChange={handleCfg} />
                </div>
              ))}
            </div>

            {msg && <p style={s.error}>{msg}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button style={s.btnPrimary} onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando...' : editId ? 'Actualizar' : 'Guardar cuenta'}
              </button>
              <button style={s.btnSecondary}
                onClick={() => { setMostrarForm(false); setMsg(''); }}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, val }) {
  return (
    <div style={{ display: 'flex', gap: 6, fontSize: 12, color: '#555', marginBottom: 3 }}>
      <span style={{ color: '#aaa', width: 70, flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 500, color: '#333' }}>{val}</span>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui,sans-serif' },
  nav: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 24px', height: 56,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brand: { fontWeight: 700, fontSize: 16 },
  navRight: { display: 'flex', alignItems: 'center', gap: 12 },
  navUser: { fontSize: 13, color: '#555' },
  navLink: { fontSize: 13, color: '#2563eb', textDecoration: 'none' },
  navBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
    background: 'transparent', cursor: 'pointer', fontSize: 13 },
  content: { padding: '28px 24px', maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, margin: '0 0 4px', color: '#111' },
  subtitle: { fontSize: 13, color: '#666', margin: 0 },
  empty: { textAlign: 'center', padding: '60px 0', color: '#888', display: 'flex',
    flexDirection: 'column', alignItems: 'center', gap: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16, marginBottom: 24 },
  card: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden' },
  cardHead: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px', background: '#f8fafc',
    borderBottom: '1px solid #e5e7eb' },
  cardIcon: { fontSize: 24 },
  cardNombre: { fontWeight: 600, fontSize: 14, color: '#111' },
  cardBanco: { fontSize: 12, color: '#666', marginTop: 2 },
  cardBody: { padding: '12px 16px' },
  cardFoot: { display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid #f0f0f0' },
  btnEdit: { flex: 1, padding: '6px', borderRadius: 7, background: '#eff6ff', color: '#2563eb',
    border: '1px solid #bfdbfe', cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  btnDel: { flex: 1, padding: '6px', borderRadius: 7, background: '#fff5f5', color: '#dc2626',
    border: '1px solid #fee2e2', cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  formWrap: { background: '#fff', borderRadius: 14, padding: '24px', border: '1px solid #e5e7eb' },
  formTitle: { fontSize: 16, fontWeight: 600, margin: '0 0 20px', color: '#111' },
  section: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 10, marginTop: 16, paddingBottom: 6,
    borderBottom: '1px solid #f0f0f0' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#444', margin: '0 0 10px', cursor: 'pointer' },
  label: { fontSize: 11, color: '#666', fontWeight: 500 },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 },
  error: { color: '#dc2626', fontSize: 13, margin: '8px 0 0' },
  btnPrimary: { padding: '10px 20px', borderRadius: 9, background: '#2563eb', color: '#fff',
    border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  btnSecondary: { padding: '10px 20px', borderRadius: 9, background: '#fff', color: '#444',
    border: '1px solid #e5e7eb', fontWeight: 500, fontSize: 14, cursor: 'pointer' },
};
